import React, { useState } from 'react';
import type { StudentNoteItem } from '../../../types/orchestrator';
import { canDeleteStudentNote } from '../../../utils/studentNotes';
import ConfirmModal from '../ConfirmModal/ConfirmModal';
import StudentNoteHistoryItem from './StudentNoteHistoryItem';
import styles from './StudentNotesHistorySection.module.css';

interface StudentNotesHistorySectionProps {
  notes: StudentNoteItem[];
  title?: string;
  titleAction?: React.ReactNode;
  fillHeight?: boolean;
  disableScroll?: boolean;
  compact?: boolean;
  footer?: React.ReactNode;
  emptyMessage?: string;
  onDeleteNote?: (noteId: number) => Promise<void>;
}

const StudentNotesHistorySection: React.FC<StudentNotesHistorySectionProps> = ({
  notes,
  title = 'Observações registradas',
  emptyMessage = 'Nenhuma observação registrada',
  titleAction,
  fillHeight = false,
  disableScroll = false,
  compact = false,
  footer,
  onDeleteNote,
}) => {
  const [pendingDeleteNote, setPendingDeleteNote] = useState<StudentNoteItem | null>(null);
  const [deleting, setDeleting] = useState(false);

  const handleConfirmDelete = async () => {
    if (!pendingDeleteNote || !onDeleteNote) {
      return;
    }

    setDeleting(true);
    try {
      await onDeleteNote(pendingDeleteNote.id);
      setPendingDeleteNote(null);
    } catch (error) {
      alert(error instanceof Error ? error.message : 'Não foi possível excluir a observação.');
    } finally {
      setDeleting(false);
    }
  };

  const sectionClassName = [styles.section, fillHeight ? styles.sectionFill : ''].filter(Boolean).join(' ');
  const historyClassName = [
    styles.history,
    disableScroll ? styles.historyNoScroll : '',
    compact ? styles.historyCompact : '',
  ].filter(Boolean).join(' ');

  const header = (title || titleAction) ? (
    <div className={styles.headerRow}>
      {title ? <span className={styles.title}>{title}</span> : null}
      {titleAction}
    </div>
  ) : null;

  if (notes.length === 0) {
    return (
      <div className={sectionClassName}>
        {header}
        <p className={styles.empty}>{emptyMessage}</p>
        {footer}
      </div>
    );
  }

  return (
    <div className={sectionClassName}>
      {header}
      <div className={historyClassName}>
        {notes.map((noteItem) => (
          <StudentNoteHistoryItem
            key={noteItem.id}
            note={noteItem}
            canDelete={Boolean(onDeleteNote) && canDeleteStudentNote(noteItem)}
            deleting={deleting}
            onDelete={() => setPendingDeleteNote(noteItem)}
          />
        ))}
      </div>
      {footer}

      <ConfirmModal
        open={Boolean(pendingDeleteNote)}
        title="Excluir observação"
        confirmLabel="Excluir"
        cancelLabel="Cancelar"
        variant="danger"
        loading={deleting}
        onCancel={() => {
          if (!deleting) {
            setPendingDeleteNote(null);
          }
        }}
        onConfirm={handleConfirmDelete}
      >
        <p className={styles.confirmMessage}>Deseja excluir esta observação?</p>
      </ConfirmModal>
    </div>
  );
};

export default StudentNotesHistorySection;
